import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import type { ExportStreetSurveyInput, ExportStreetSurveyResult } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * exportStreetSurvey callable hook — 机上調査 (ストリートビュー / 360度写真) の結果の出力
 * (オーナー要望 2026-09-18、docs/98 R-99)。useExportPosts と同 pattern。
 */

export type UseExportStreetSurveyState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: ExportStreetSurveyResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseExportStreetSurveyReturn {
  state: UseExportStreetSurveyState;
  call: (input: ExportStreetSurveyInput) => Promise<ExportStreetSurveyResult | null>;
  reset: () => void;
}

export function useExportStreetSurvey(): UseExportStreetSurveyReturn {
  const [state, setState] = useState<UseExportStreetSurveyState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function call(input: ExportStreetSurveyInput): Promise<ExportStreetSurveyResult | null> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const fn = httpsCallable<ExportStreetSurveyInput, ExportStreetSurveyResult>(
        functions,
        'exportStreetSurvey',
        // 地点数が多い地区 (本番 逗子で 293 点) でも打ち切られないよう長めに取る。
        { timeout: 300_000 },
      );
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = e as Error;
      setState({ status: 'error', result: null, error: err });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, call, reset };
}

/** CSV / GeoJSON content をブラウザ download として起動する (CSV は Excel 向けに BOM 付与)。 */
export function triggerStreetSurveyDownload(result: ExportStreetSurveyResult): void {
  const mime =
    result.format === 'csv' ? 'text/csv;charset=utf-8' : 'application/geo+json;charset=utf-8';
  const parts = result.format === 'csv' ? ['﻿', result.content] : [result.content];
  const blob = new Blob(parts, { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  a.click();
  URL.revokeObjectURL(url);
}
